/**
 * 기사앱 목록 캐시 + 미리 불러오기(2026-09-27 way 「메뉴 누를 때마다 불러오기가 나와 느리다고 민원이 올 것」).
 *
 * 왜: 기사앱 함수는 담당자 웹과 달리 **워밍이 하나도 없었다**(`lib/warm.ts` 는 담당자 함수만). callable 하나가
 * 컨테이너 하나라, 쓰지 않은 지 ~15분이면 내려가 첫 탭이 2~5초 「불러오는 중…」이었다.
 *
 * 그래서 두 가지를 같이 한다.
 *  ① **보여 줄 것을 먼저 받아 둔다** — 홈이 뜨면 입찰·내 신청·공지·명세서 목록을 한꺼번에 받아 둔다.
 *     화면은 받아 둔 것을 **바로** 그리고, 들어갈 때마다 뒤에서 새로 받아 갈아 끼운다(오래된 값을 오래 보이지 않게).
 *  ② **안 보이는 함수는 깨워만 둔다** — 공지 본문·명세 상세·다른 앱 연결은 목록이 아니라 미리 받을 수 없으니
 *     **거절되는 인자**로 불러 컨테이너만 띄운다(공지 본문은 부르면 「읽음」이 기록되므로 실제 id 를 쓰지 않는다).
 *  앱을 보고 있는 동안만 10분마다 되풀이한다(컨테이너 유휴 수명 ~15분보다 짧게). 로그아웃하면 비운다.
 *
 * 쓰기(입찰 신청·취소) 뒤에는 화면이 `reload()` 로 다시 받는다 — 캐시가 쓰기 결과를 가리지 않게.
 */

import { useCallback, useEffect, useState } from 'react';
import { driverApi, driverError, getActingDriverId } from './driverApi';

const store = new Map<string, unknown>();
const inflight = new Map<string, Promise<unknown>>();

/**
 * 차주가 다른 기사를 고르면(#258) 명세서 목록이 그 기사 것이 된다 — 캐시 칸을 사람별로 나눈다.
 * 안 나누면 고른 직후 한 순간 본인 명세가 그 기사 이름 아래 보인다.
 */
const slot = (key: string) => `${key}:${getActingDriverId() ?? 'self'}`;

function fetchCached<T>(rawKey: string, fn: () => Promise<T>): Promise<T> {
  const key = slot(rawKey);
  const running = inflight.get(key) as Promise<T> | undefined;
  if (running) return running;
  const p = fn()
    .then((v) => {
      store.set(key, v);
      return v;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** 화면에서 쓰는 목록 — 키와 불러오기를 한곳에 둔다(미리 불러오기와 화면이 같은 값을 보게). */
export const DRIVER_LISTS = {
  bids: () => driverApi.listBids({ limit: 100 }),
  myBids: () => driverApi.listMyBids({ limit: 100 }),
  // 이번 달만 미리 받는다 — 다른 달은 화면이 넘길 때 받는다.
  myDispts: () => driverApi.listMyDispts({}),
  notices: () => driverApi.listNotices({ limit: 100 }),
  salaryMonths: () => driverApi.listSalaryMonths(),
} as const;
type ListKey = keyof typeof DRIVER_LISTS;
type ListOf<K extends ListKey> = Awaited<ReturnType<(typeof DRIVER_LISTS)[K]>>;

/**
 * 받아 둔 값이 있으면 바로 주고(로딩 없음), 화면에 들어올 때마다 뒤에서 새로 받는다.
 * `data === undefined` 일 때만 「불러오는 중」 — 처음 한 번뿐이다.
 */
export function useDriverList<K extends ListKey>(key: K, fallbackError: string) {
  const [data, setData] = useState<ListOf<K> | undefined>(() => store.get(slot(key)) as ListOf<K> | undefined);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const v = await fetchCached(key, DRIVER_LISTS[key] as () => Promise<ListOf<K>>);
      setData(v);
      setError(null);
    } catch (e) {
      setError(driverError(e, fallbackError));
      // 받아 둔 값이 있으면 그대로 두고 오류만 알린다. 없으면 빈 목록으로 「없습니다」 대신 오류가 보이게.
      setData((d) => d ?? ({ rows: [] } as unknown as ListOf<K>));
    }
  }, [key, fallbackError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, reload };
}

/** 쓰기 뒤 다른 화면의 목록을 뒤에서 새로 받아 둔다(그 화면에 들어가면 새 값이 바로 보이게). */
export function refreshDriverList(key: ListKey): void {
  void fetchCached(key, DRIVER_LISTS[key] as () => Promise<unknown>).catch(() => undefined);
}

/** 삼키기 — 워밍은 화면에 아무 영향도 주면 안 된다. */
const quiet = (p: Promise<unknown>) => p.then(
  () => undefined,
  () => undefined,
);

function wave(): void {
  for (const key of Object.keys(DRIVER_LISTS) as ListKey[]) void quiet(fetchCached(key, DRIVER_LISTS[key] as () => Promise<unknown>));
  // 목록이 아닌 화면 — 거절되는 인자로 컨테이너만 깨운다(아무것도 쓰지 않는다).
  void quiet(driverApi.getNotice({ noticeId: 0 }));
  void quiet(driverApi.listSalaryLines({ driveMon: '2000-01' }));
  void quiet(driverApi.issueHandoff({ target: 'warm' as never }));
}

const REWARM_MS = 10 * 60_000;
let timer: ReturnType<typeof setInterval> | null = null;

/** 홈이 뜰 때 부른다. 여러 번 불러도 타이머는 하나. */
export function startDriverPrefetch(): void {
  wave();
  if (timer) return;
  timer = setInterval(() => {
    if (document.visibilityState === 'visible') wave();
  }, REWARM_MS);
}

/** 로그아웃·다른 기사 로그인 — 남의 목록이 다음 사람 화면에 한 순간도 보이지 않게. */
export function clearDriverCache(): void {
  store.clear();
  inflight.clear();
  if (timer) clearInterval(timer);
  timer = null;
}
